import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material'
import type { InventoryLeadLostReason } from '@otw/models/leads/inventoryLead'
import { useInventoryLeadLostReasons } from 'hooks/admin/useInventoryLeadLostReasons'

export type InventoryLeadLostReasonSelection = {
  reasonId: string
  reasonLabel: string
  notes: string | null
}

export interface InventoryLeadLostReasonDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (selection: InventoryLeadLostReasonSelection) => void | Promise<void>
  /** Optional context shown under the title. */
  leadLabel?: string
}

type ReasonOption = InventoryLeadLostReason | { id: string; label: string; inputValue: string }

const filter = createFilterOptions<ReasonOption>()

/** Capture why an inventory lead was lost — pick an existing reason or create one. */
const InventoryLeadLostReasonDialog: React.FC<InventoryLeadLostReasonDialogProps> = ({
  open,
  onClose,
  onConfirm,
  leadLabel,
}) => {
  const { reasons, isLoading, createLostReason, isCreating } = useInventoryLeadLostReasons(open)
  const [selected, setSelected] = useState<ReasonOption | null>(null)
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      setSelected(null)
      setNotes('')
      setIsSubmitting(false)
    }
  }, [open])

  const options = useMemo(() => reasons as ReasonOption[], [reasons])

  const resolvedLabel = (() => {
    if (!selected) return ''
    if ('inputValue' in selected && selected.inputValue) return selected.inputValue.trim()
    return selected.label.trim()
  })()

  const canSubmit = resolvedLabel.length > 0 && !isSubmitting && !isCreating

  const handleConfirm = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      let reasonId = 'id' in selected! && !('inputValue' in selected) ? selected!.id : ''
      let reasonLabel = resolvedLabel

      if (!reasonId || ('inputValue' in (selected ?? {}) && (selected as { inputValue?: string }).inputValue)) {
        const created = await createLostReason(reasonLabel)
        reasonId = created.id
        reasonLabel = created.label
      }

      await onConfirm({
        reasonId,
        reasonLabel,
        notes: notes.trim() || null,
      })
      onClose()
    } catch {
      // Snackbar handling lives in the hooks / callers.
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Why was this lead lost?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {leadLabel ? (
            <Typography variant="body2" color="text.secondary">
              {leadLabel}
            </Typography>
          ) : null}
          <Autocomplete
            value={selected}
            onChange={(_event, next) => setSelected(next)}
            filterOptions={(opts, params) => {
              const filtered = filter(opts, params)
              const input = params.inputValue.trim()
              if (!input) return filtered
              const exists = opts.some(
                (opt) => opt.label.localeCompare(input, undefined, { sensitivity: 'base' }) === 0,
              )
              if (!exists) {
                filtered.push({
                  id: `new:${input}`,
                  label: `Create "${input}"`,
                  inputValue: input,
                })
              }
              return filtered
            }}
            options={options}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option
              if ('inputValue' in option && option.inputValue) return option.inputValue
              return option.label
            }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={isLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Lost reason"
                required
                placeholder="Select or type a new reason"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            freeSolo={false}
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            placeholder="Any extra context for the team..."
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              New reasons are saved for everyone to reuse next time.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting || isCreating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleConfirm()}
          disabled={!canSubmit}
          startIcon={isSubmitting || isCreating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSubmitting || isCreating ? 'Saving…' : 'Mark as lost'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default InventoryLeadLostReasonDialog
